import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMemberUna } from "@/hooks/useMemberUna";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Plus, Printer, FileText, Building2 } from "lucide-react";
import { humanOfficeStatus } from "@/lib/succession/stages";

/**
 * Office Builder: create, adopt, and record the Association's offices.
 * The two constitutional offices are shown as read-only informational rows.
 */

interface OfficeRow {
  id: string;
  office_name: string;
  purpose: string | null;
  authority_granted: string | null;
  limits_text: string | null;
  reports_to: string | null;
  term_text: string | null;
  holder_name: string | null;
  status: "proposed" | "active" | "vacant" | "retired";
  effective_date: string | null;
}

const STANDING_LIMITS = [
  "The power of this office comes from the Constitution and from written decisions of the Board. It never comes from the title alone.",
  "If this officer would gain from a decision, they step out of the vote on it, and the minutes say so.",
  "Any pay or benefit to this officer needs its own written decision and its own written test.",
];

type CharterKey =
  | "recording_secretary"
  | "chief_investment_steward"
  | "chief_strategy_steward"
  | "education_steward"
  | "steward_protector"
  | "blank";

const CHARTERS: Record<CharterKey, {
  label: string;
  office_name: string;
  purpose: string;
  authority_granted: string;
  limits_text: string;
  reports_to: string;
  term_text: string;
}> = {
  recording_secretary: {
    label: "Recording Secretary",
    office_name: "Recording Secretary",
    purpose: "Keep the meeting minutes and notices of the Association, so every decision has its written record.",
    authority_granted: "Prepare and send out meeting notices. Take minutes at every meeting of the Board and the Voting Members. Sign minutes for the Record after the officers have read them.",
    limits_text: "Holds no say over money or property. Signs no papers outside the meeting record. Passes none of these jobs to someone else without a written decision of the Board.",
    reports_to: "The Principal Executive Officer, and through them the Board.",
    term_text: "At the pleasure of the Board.",
  },
  chief_investment_steward: {
    label: "Chief Investment Steward",
    office_name: "Chief Investment Steward",
    purpose: "Study and give advice on the Association's savings, under the written rule the Board has set.",
    authority_granted: "Study how the savings are cared for. Bring written advice to the Board for a decision. Work with the Association's licensed professionals on anything the law calls regulated.",
    limits_text: "Places no trades and gives no advice on stocks or bonds. Cannot sign on any account. Every idea goes to the Board for a written decision. Anything that needs a license goes to a licensed professional.",
    reports_to: "The Board.",
    term_text: "At the pleasure of the Board.",
  },
  chief_strategy_steward: {
    label: "Chief Strategy Steward",
    office_name: "Chief Strategy Steward",
    purpose: "Study the long horizon of the mission and bring plans to the Board.",
    authority_granted: "Study the mission across five, ten, and twenty five year windows. Draft plans, choices, and priorities. Bring ideas to the Board for a decision.",
    limits_text: "Does nothing without a written decision. Cannot bind the Association.",
    reports_to: "The Board.",
    term_text: "At the pleasure of the Board.",
  },
  education_steward: {
    label: "Education Steward",
    office_name: "Education Steward",
    purpose: "Lead the teaching rhythm for the household and the community.",
    authority_granted: "Plan and lead the study rhythm. Care for the teaching materials. Report progress to the Board.",
    limits_text: "Speaks for the Association only when the Board has said so in writing.",
    reports_to: "The Principal Executive Officer.",
    term_text: "At the pleasure of the Board.",
  },
  steward_protector: {
    label: "Steward Protector",
    office_name: "Steward Protector",
    purpose: "Watch over the handoff between stewards. Rests quietly while officers serve, and steps forward only at set moments.",
    authority_granted: "Confirm incapacity, meaning a season when an officer cannot serve, under the Association's written rule. Turn on the continuity naming when its trigger is met. Break a Board tie after a seat opens. Call the Emergency Assembly under the Constitution.",
    limits_text: "Never holds the Association's things. Never votes in regular business. Never serves as a successor at the same time. The Board may remove this officer under the Constitution.",
    reports_to: "The Association's Voting Members.",
    term_text: "At the pleasure of the Board.",
  },
  blank: {
    label: "Start blank",
    office_name: "",
    purpose: "",
    authority_granted: "",
    limits_text: "",
    reports_to: "",
    term_text: "At the pleasure of the Board.",
  },
};

const CONSTITUTIONAL_OFFICES = [
  {
    office_name: "Principal Executive Officer",
    purpose: "Leads and carries the day to day work of the Association under the Constitution and the written decisions of the Board.",
    marker: "Set by your Constitution",
  },
  {
    office_name: "Secretary Treasurer",
    purpose: "Keeps the Record and the books, and prepares the reporting rhythm the Constitution asks for.",
    marker: "Set by your Constitution",
  },
];

export default function GovernanceOffices() {
  const { user } = useAuth();
  const { unaId, unaName } = useMemberUna();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offices, setOffices] = useState<OfficeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);

  const preloadCharter = (searchParams.get("charter") as CharterKey | null) || null;

  useEffect(() => {
    if (preloadCharter && CHARTERS[preloadCharter]) {
      setShowBuilder(true);
    }
  }, [preloadCharter]);

  async function load() {
    if (!unaId) return;
    setLoading(true);
    const { data } = await supabase
      .from("association_offices")
      .select("*")
      .eq("association_id", unaId)
      .order("created_at", { ascending: true });
    setOffices((data as OfficeRow[]) || []);
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unaId]);

  return (
    <MainLayout>
      <div className="container max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Link to="/governance" className="hover:underline">Governance</Link>
              <span>/</span>
              <span>Offices</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight title-rule">Offices</h1>
            <p className="text-sm text-muted-foreground mt-1">
              The offices of {unaName || "your Association"}, and their charters.
            </p>
          </div>
          {!showBuilder && (
            <Button onClick={() => setShowBuilder(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create an office
            </Button>
          )}
        </div>

        {showBuilder ? (
          <OfficeBuilder
            unaId={unaId}
            unaName={unaName || ""}
            userId={user?.id || null}
            preload={preloadCharter && CHARTERS[preloadCharter] ? preloadCharter : null}
            onDone={() => {
              setShowBuilder(false);
              if (preloadCharter) {
                const next = new URLSearchParams(searchParams);
                next.delete("charter");
                setSearchParams(next, { replace: true });
              }
              load();
            }}
            onCancel={() => setShowBuilder(false)}
          />
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Constitutional offices</CardTitle>
                <CardDescription>Defined by your Constitution and always present.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {CONSTITUTIONAL_OFFICES.map((o) => (
                  <div key={o.office_name} className="border rounded-md p-4 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary" />
                        <span className="font-medium">{o.office_name}</span>
                      </div>
                      <Badge variant="secondary">{o.marker}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{o.purpose}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Created offices</CardTitle>
                <CardDescription>Offices your Board has created by resolution.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : offices.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">No offices created yet.</p>
                ) : (
                  <div className="space-y-3">
                    {offices.map((o) => <OfficeCard key={o.id} office={o} onChanged={load} unaName={unaName || ""} />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </MainLayout>
  );
}

function OfficeCard({ office, onChanged, unaName }: { office: OfficeRow; onChanged: () => void; unaName: string }) {
  const [busy, setBusy] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  async function markAdopted() {
    setBusy(true);
    await supabase
      .from("association_offices")
      .update({ status: "active", effective_date: new Date().toISOString().slice(0, 10) })
      .eq("id", office.id);
    setBusy(false);
    toast.success("Office marked adopted. File the signed originals in the Record.");
    onChanged();
  }

  return (
    <div className="border rounded-md p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{office.office_name}</span>
            <Badge variant={office.status === "active" ? "default" : "secondary"}>{humanOfficeStatus(office.status)}</Badge>
            {office.effective_date && (
              <span className="text-xs text-muted-foreground">in effect since {office.effective_date}</span>
            )}
          </div>
          {office.holder_name && (
            <p className="text-xs text-muted-foreground mt-1">Held by {office.holder_name}</p>
          )}
          {office.purpose && <p className="text-sm text-muted-foreground mt-2">{office.purpose}</p>}
        </div>
        <div className="flex flex-col gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowPrint((s) => !s)}>
            <Printer className="w-4 h-4 mr-1" /> {showPrint ? "Hide" : "Print view"}
          </Button>
          {office.status === "proposed" && (
            <Button size="sm" onClick={markAdopted} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Mark adopted
            </Button>
          )}
        </div>
      </div>
      {showPrint && (
        <div className="mt-4">
          <PrintableCharter office={office} unaName={unaName} />
        </div>
      )}
    </div>
  );
}

function PrintableCharter({ office, unaName }: { office: OfficeRow; unaName: string }) {
  return (
    <div className="ecclesiastical-document bg-white p-8 border rounded-md text-black">
      <div className="text-center mb-6">
        <h3 className="text-xl font-serif uppercase tracking-wide">{unaName || "The Association"}</h3>
        <h4 className="text-lg font-serif mt-2">Resolution Creating the Office of {office.office_name}</h4>
      </div>

      <h5 className="font-serif text-base mb-2">Why we are doing this</h5>
      <p className="mb-4">
        The Constitution of {unaName || "the Association"} lets the Board create offices by written decision. The Board has looked at the work of the Association and decided that the office described below serves the mission.
      </p>

      <h5 className="font-serif text-base mb-2">What the Board decides</h5>
      <ol className="list-decimal pl-6 space-y-3 mb-6">
        <li>
          <p>The office of {office.office_name} is created with this charter:</p>
          <div className="pl-4 mt-2 space-y-2">
            <p><strong>Purpose.</strong> {office.purpose}</p>
            <p><strong>What this office may do.</strong> {office.authority_granted}</p>
            <p><strong>Limits.</strong> {office.limits_text}</p>
            <p><strong>Reports to.</strong> {office.reports_to}</p>
            <p><strong>Term.</strong> {office.term_text}</p>
            {office.holder_name && <p><strong>First holder.</strong> {office.holder_name}</p>}
          </div>
        </li>
        <li>
          <p>This office is bound by these standing limits:</p>
          <ol className="list-[lower-alpha] pl-6 mt-2 space-y-1">
            {STANDING_LIMITS.map((l, i) => <li key={i}>{l}</li>)}
          </ol>
        </li>
      </ol>

      <div className="mt-10 grid grid-cols-2 gap-8">
        <div>
          <div className="border-b border-black h-8" />
          <p className="text-sm mt-1">Principal Executive Officer</p>
        </div>
        <div>
          <div className="border-b border-black h-8" />
          <p className="text-sm mt-1">Date</p>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-black">
        <h4 className="text-lg font-serif text-center mb-4">Officer Acceptance</h4>
        <p className="mb-3">
          I, {office.holder_name || "____________________"}, accept the office of {office.office_name} as written in this charter. I have read the purpose, what the office may do, the limits, and the standing limits above, and I accept each of them.
        </p>
        <p className="text-sm italic mb-6">Sign and date in blue ink. Have a notary stamp the acceptance (a notary is the officer who stamps to prove you signed). File both in the Record.</p>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="border-b border-black h-8" />
            <p className="text-sm mt-1">Officer signature</p>
          </div>
          <div>
            <div className="border-b border-black h-8" />
            <p className="text-sm mt-1">Date</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Builder wizard ---------- */
function OfficeBuilder({
  unaId,
  unaName,
  userId,
  preload,
  onDone,
  onCancel,
}: {
  unaId: string | null;
  unaName: string;
  userId: string | null;
  preload: CharterKey | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<0 | 1 | 2 | 3>(preload ? 2 : 0);
  const [choice, setChoice] = useState<CharterKey>(preload || "blank");
  const [form, setForm] = useState(() => {
    const base = CHARTERS[preload || "blank"];
    return {
      office_name: base.office_name,
      purpose: base.purpose,
      authority_granted: base.authority_granted,
      limits_text: base.limits_text,
      reports_to: base.reports_to,
      term_text: base.term_text,
      holder_name: "",
    };
  });
  const [saving, setSaving] = useState(false);

  function loadCharter(key: CharterKey) {
    setChoice(key);
    const c = CHARTERS[key];
    setForm({
      office_name: c.office_name,
      purpose: c.purpose,
      authority_granted: c.authority_granted,
      limits_text: c.limits_text,
      reports_to: c.reports_to,
      term_text: c.term_text,
      holder_name: "",
    });
    setStep(2);
  }

  async function save(markAdopted: boolean) {
    if (!unaId || !userId) return;
    if (!form.office_name.trim()) {
      toast.error("Please give the office a name.");
      return;
    }
    setSaving(true);
    const payload: any = {
      association_id: unaId,
      created_by: userId,
      office_name: form.office_name.trim(),
      purpose: form.purpose || null,
      authority_granted: form.authority_granted || null,
      limits_text: form.limits_text || null,
      reports_to: form.reports_to || null,
      term_text: form.term_text || null,
      holder_name: form.holder_name || null,
      status: markAdopted ? "active" : "proposed",
      effective_date: markAdopted ? new Date().toISOString().slice(0, 10) : null,
      charter: { standing_limits: STANDING_LIMITS, source: choice },
    };
    const { error } = await supabase.from("association_offices").insert(payload);
    setSaving(false);
    if (error) {
      toast.error("Could not save the office.");
      return;
    }
    toast.success(markAdopted ? "Office adopted. File the signed originals in the Record." : "Office saved as proposed.");
    onDone();
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Create an office</CardTitle>
            <CardDescription>Step {step + 1} of 4</CardDescription>
          </div>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {step === 0 && (
          <div className="space-y-4">
            <div className="prose prose-sm max-w-none">
              <p>
                Your Constitution lets the Board create offices by written decision. Every office needs four things written down: what it is for, what it may do, what it may not do, and who it answers to. An office that may do without limits is a problem waiting for a bad day.
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep(1)}>
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Pick a prepared charter to start from, or start blank.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(Object.keys(CHARTERS) as CharterKey[]).map((k) => (
                <button
                  key={k}
                  onClick={() => loadCharter(k)}
                  className="text-left border rounded-md p-4 hover:bg-accent transition"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{CHARTERS[k].label}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                  {CHARTERS[k].purpose && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{CHARTERS[k].purpose}</p>
                  )}
                </button>
              ))}
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Office name</Label>
              <Input value={form.office_name} onChange={(e) => setForm({ ...form, office_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Textarea rows={3} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>What this office may do</Label>
              <Textarea rows={3} value={form.authority_granted} onChange={(e) => setForm({ ...form, authority_granted: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Limits</Label>
              <Textarea rows={3} value={form.limits_text} onChange={(e) => setForm({ ...form, limits_text: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Reports to</Label>
                <Input value={form.reports_to} onChange={(e) => setForm({ ...form, reports_to: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Term</Label>
                <Input value={form.term_text} onChange={(e) => setForm({ ...form, term_text: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Holder name (optional)</Label>
              <Input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} placeholder="Leave blank if not yet named" />
            </div>
            <div className="rounded-md bg-muted/40 p-4 space-y-2">
              <p className="text-sm font-medium">Standing limits included on every office</p>
              <ol className="list-decimal pl-6 space-y-1 text-sm text-muted-foreground">
                {STANDING_LIMITS.map((l, i) => <li key={i}>{l}</li>)}
              </ol>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <Button onClick={() => setStep(3)}>Review <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <PrintableCharter
              office={{
                id: "preview",
                office_name: form.office_name,
                purpose: form.purpose,
                authority_granted: form.authority_granted,
                limits_text: form.limits_text,
                reports_to: form.reports_to,
                term_text: form.term_text,
                holder_name: form.holder_name || null,
                status: "proposed",
                effective_date: null,
              }}
              unaName={unaName}
            />
            <div className="flex flex-wrap justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => save(false)} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save as proposed
                </Button>
                <Button onClick={() => save(true)} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Mark adopted
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
